import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, TextField, Alert, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../../api/api';
import { ReturnCalculation, money } from '../../../client/ClientReturn';
import ReturnPointSelect from './ReturnPointSelect';

const localNow = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Keep the user's wall time and its offset: online rentals normalize it to UTC,
// while the existing offline return code stores local, timezone-naive dates.
const withOffset = value => {
  const offset = -new Date(value).getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  return `${value}:00${sign}${String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0')}:${String(Math.abs(offset) % 60).padStart(2, '0')}`;
};

const AssetReturnModal = ({ open, onClose, dealId, asset, onAssetReturned }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [returnDate, setReturnDate] = useState(localNow);
  const [attempt, setAttempt] = useState(0);
  const [rentalPointId, setRentalPointId] = useState('');
  const assetId = asset?.id;
  useEffect(() => { if (open) { setReturnDate(localNow()); setComment(''); setPreview(null); setError(''); setRentalPointId(''); } }, [open, dealId, assetId]);
  useEffect(() => {
    if (!open || !dealId || !assetId) return;
    let active = true;
    const controller = new AbortController();
    setPreview(null); setError('');
    if (!returnDate || Number.isNaN(new Date(returnDate).getTime())) { setError('Укажите дату и время сдачи.'); setLoading(false); return; }
    setLoading(true);
    api.get(`/rental/asset-returns/${dealId}/preview-return/${assetId}`, {
      params: { return_date: withOffset(returnDate) }, signal: controller.signal, skipLoading: true,
    }).then(res => { if (active) setPreview(res.data); })
      .catch(e => { if (active) setError(e.response?.data?.detail || 'Не удалось получить расчёт возврата.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [open, dealId, assetId, returnDate, attempt]);

  const received = preview?.return_info?.received_at;
  const returnAlreadyReceived = !!received || ['received', 'refund_pending', 'completed'].includes(preview?.return_info?.status);
  const returnPointRequired = !returnAlreadyReceived && !!(preview?.return_point_required || preview?.return_info?.return_point_required);
  const pointMissing = returnPointRequired && !rentalPointId;
  const handleReturn = async (receiveOnly = false) => {
    if (pointMissing) { setError('Выберите фактический ПВЗ возврата.'); return; }
    setSaving(true); setError('');
    try {
      const response = await api.post(`/rental/asset-returns/${dealId}/return-asset/${assetId}`, {
        return_date: withOffset(returnDate), comment, receive_only: receiveOnly,
        ...(!returnAlreadyReceived && rentalPointId ? { rental_point_id: Number(rentalPointId) } : {}),
      }, { skipLoading: true });
      if (onAssetReturned) await onAssetReturned(assetId, response.data);
      enqueueSnackbar(response.data.message || 'Вещь возвращена', { variant: 'success' });
      onClose();
    } catch (e) { setError(e.response?.data?.detail || 'Не удалось подтвердить возврат.'); }
    finally { setSaving(false); }
  };
  if (!asset) return null;
  return <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
    <DialogTitle>Возврат вещи<Typography variant="body2" color="text.secondary">{asset.name}</Typography></DialogTitle>
    <DialogContent>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}<Button onClick={() => setAttempt(v => v + 1)}>Повторить расчёт</Button></Alert>}
      {received ? <Alert severity="info" sx={{ mb: 2 }}>Вещь принята {new Date(received).toLocaleString('ru-RU')}. Расчёт зафиксирован на это время.</Alert> : <TextField label="Фактическая дата и время сдачи" type="datetime-local" value={returnDate} onChange={e => setReturnDate(e.target.value)} fullWidth disabled={saving} sx={{ mt: 1, mb: 2 }} InputLabelProps={{ shrink: true }} helperText="Местное время устройства. Ожидание проверки после сдачи не оплачивается." />}
      {loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>}
      {preview?.settlement ? <><ReturnCalculation value={preview.settlement} /><Alert severity="info" sx={{ mb: 2 }}>Возврат переплаты пока тестовый — реального перевода денег нет.</Alert></> : preview && <Box sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography>Стоимость использования: <b>{money(preview.final_cost)}</b></Typography>
        <Typography>Доступно из платежей: {money(preview.available_amount)}</Typography>
        <Typography>Будет учтено как оплачено: {money(preview.amount_to_record)}</Typography>
        <Typography>К доплате: <b>{money(preview.remaining_debt)}</b></Typography>
      </Box>}
      {preview && !returnAlreadyReceived && <ReturnPointSelect value={rentalPointId} onChange={setRentalPointId} required={returnPointRequired} disabled={saving || loading} />}
      <TextField label="Комментарий о состоянии вещи" value={comment} onChange={e => setComment(e.target.value)} fullWidth multiline rows={2} disabled={saving} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Подтверждайте возврат после приёма и проверки вещи. Она сразу станет доступна для следующей аренды.{preview?.settlement ? ' Этот договор будет закрыт.' : ''}</Typography>
    </DialogContent>
    <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <Button onClick={onClose} disabled={saving}>Отмена</Button>
      {preview?.settlement && !returnAlreadyReceived && <Button disabled={loading || saving || !preview?.can_return || pointMissing} onClick={() => handleReturn(true)}>Принять на проверку</Button>}
      <Button onClick={() => handleReturn(false)} variant="contained" disabled={loading || saving || !preview?.can_return || pointMissing}>{saving ? 'Сохраняем…' : 'Подтвердить возврат'}</Button>
    </DialogActions>
  </Dialog>;
};

export default AssetReturnModal;
